"use client"

import { type RefObject, useRef, useState } from "react"
import { useDraggable } from "@neodrag/react"
import { createPortal } from "react-dom"
import { Chat } from "@/components/widget/chat"
import { Home } from "@/components/widget/home"
import { useMeridialStore } from "@/store"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import {
  COLLAPSED_HEIGHT,
  COLLAPSED_WIDTH,
  WIDGET_HEIGHT,
  WIDGET_WIDTH,
} from "@/lib/constants"

export function Widget() {
  const draggableRef = useRef<HTMLDivElement>(null)
  useDraggable(draggableRef as RefObject<HTMLDivElement>, {
    axis: "both",
    bounds: "body",
    cancel: ".cancel-drag",
  })

  const [state, setState] = useState<"home" | "chat">("home")
  const [direction, setDirection] = useState<"forward" | "back">("forward")
  const [selectedQuestion, setSelectedQuestion] = useState<string | undefined>(
    undefined
  )
  const [isCollapsed, setIsCollapsed] = useState(false)
  const shouldReduceMotion = useReducedMotion()

  const isChatCollapsed = state === "chat" && isCollapsed
  const widgetWidth = isChatCollapsed ? COLLAPSED_WIDTH : WIDGET_WIDTH
  const widgetHeight = isChatCollapsed ? COLLAPSED_HEIGHT : WIDGET_HEIGHT
  const sizeTransition = shouldReduceMotion
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 350, damping: 35 }

  const slideTransition = shouldReduceMotion
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 350, damping: 35 }

  const slideVariants = shouldReduceMotion
    ? {
        enter: { opacity: 0 },
        center: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        enter: (dir: "forward" | "back") => ({
          x: dir === "forward" ? "100%" : "-100%",
          opacity: 0,
        }),
        center: { x: 0, opacity: 1 },
        exit: (dir: "forward" | "back") => ({
          x: dir === "forward" ? "-100%" : "100%",
          opacity: 0,
        }),
      }

  function handleStartSession(question?: string) {
    setDirection("forward")
    setState("chat")
    setSelectedQuestion(question)
  }

  const setOpen = useMeridialStore((s) => s.setOpen)

  function handleDismiss() {
    setDirection("back")
    setState("home")
    setOpen(false)
  }

  function handleBackToHome() {
    setDirection("back")
    setState("home")
    setSelectedQuestion(undefined)
    setIsCollapsed(false)
  }

  return createPortal(
    <motion.div
      ref={draggableRef}
      data-meridial-ui
      className="fixed top-[calc(50%-200px)] left-[calc(50%-150px)] z-[999999] flex flex-col overflow-hidden rounded-lg border bg-background shadow-lg"
      initial={false}
      animate={{ width: widgetWidth, height: widgetHeight }}
      transition={sizeTransition}
    >
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <AnimatePresence initial={false} custom={direction}>
          {state === "home" ? (
            <motion.div
              key="home"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="absolute inset-0 flex min-h-0 flex-col"
            >
              <Home
                handleStartSession={handleStartSession}
                onClose={handleDismiss}
                selectedQuestion={selectedQuestion}
                onSelectQuestion={setSelectedQuestion}
              />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="absolute inset-0 flex min-h-0 flex-col"
            >
              <Chat
                onDisconnect={handleBackToHome}
                initialInput={selectedQuestion}
                isCollapsed={isCollapsed}
                onCollapsedChange={setIsCollapsed}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>,
    document.body
  )
}
